import React, { createContext, useContext, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../App';

/**
 * SPONSOR ROTATION CONTEXT — LOCKED DIRECTIVE
 * 
 * Rotation Triggers (Render Events):
 * - user login success
 * - route/screen change (client-side navigation)
 * 
 * NO rotation on scroll. NO timers.
 * 
 * The rotation_seed is used to deterministically select sponsors
 * without any user tracking or behavioral data.
 */

export const SponsorRotationContext = createContext({
  rotationSeed: null,
  refreshRotation: () => {},
});

// Generate a unique seed string
function generateSeed(path) {
  // Use path + random component for deterministic-ish rotation
  // NO user_id to avoid tracking
  return `${path.replace(/\//g, '_')}_${Math.random().toString(36).substring(2, 10)}`;
}

export function SponsorRotationProvider({ children }) {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  
  // Initialize seed with a function to avoid re-generating on every render
  const [rotationSeed, setRotationSeed] = useState(() => 
    user ? generateSeed(location.pathname) : null
  );
  
  // Track previous path to detect route changes
  const [prevPath, setPrevPath] = useState(location.pathname);
  
  // Update seed on route change
  if (user && location.pathname !== prevPath) {
    setPrevPath(location.pathname);
    setRotationSeed(generateSeed(location.pathname));
  }
  
  // Update seed when user logs in
  if (user && !rotationSeed) {
    setRotationSeed(generateSeed(location.pathname));
  }
  
  // Clear seed when user logs out
  if (!user && rotationSeed) {
    setRotationSeed(null);
  }

  // Manual refresh function
  const refreshRotation = useCallback(() => {
    setRotationSeed(generateSeed(location.pathname));
  }, [location.pathname]);

  return (
    <SponsorRotationContext.Provider value={{ rotationSeed, refreshRotation }}>
      {children}
    </SponsorRotationContext.Provider>
  );
}

// Hook to access rotation context
export function useSponsorRotation() {
  return useContext(SponsorRotationContext);
}

export default SponsorRotationProvider;
